// pages/Contact.tsx
import React, { useState } from 'react';
import Navbar from '../Components/Navbar';
import { Footer } from '../Components/Footer';
import { useTranslation } from '../../TranslationContext';

interface FormData {
  name: string;
  email: string;
  message: string;
}

export const Contact: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });
    
    try {
      // Simulating API call for demonstration
      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      setSubmitStatus({
        type: 'success',
        message: t('message_sent_successfully')
      });
      
      // Reset form after successful submission
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        type: 'error',
        message: t('failed_to_send_message')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gradient-to-b from-white to-[#86a3b8] min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Titre avec effets doux */}
        <h1 className="text-5xl md:text-6xl font-extrabold text-center mb-12">
          <span className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-[#d88c9a] via-[#e6a4b4] to-[#86a3b8] animate-[pulse_3s_ease-in-out_infinite]">
            {t('contact_us2')}
            <span className="absolute -top-2 -right-2 w-2 h-2 bg-[#d88c9a] rounded-full animate-ping opacity-75"></span>
          </span>
          <span className="block mt-2 h-1 w-24 mx-auto bg-gradient-to-r from-[#d88c9a] to-[#86a3b8] rounded-full animate-[pulse_4s_ease-in-out_infinite]"></span>
        </h1>

        {/* Formulaire avec nouvelle palette de couleurs */}
        <form
          onSubmit={handleSubmit}
          className="max-w-lg mx-auto bg-white p-8 rounded-xl shadow-md border border-[#f6e6d1] transform transition-all duration-500 hover:shadow-xl"
        >
          <div className="space-y-6">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-black transition-colors duration-300 hover:text-[#d88c9a]"
              >
                {t('name')}
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="mt-1 block w-full h-12 rounded-md border-[#e8eaed] shadow-sm focus:border-[#d88c9a] focus:ring-[#e6a4b4] transition-all duration-300 hover:shadow-md focus:shadow-lg bg-[#f6e6d1]/20 px-3"
              />
            </div>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-black transition-colors duration-300 hover:text-[#d88c9a]"
              >
                {t('email')}
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="mt-1 block w-full h-12 rounded-md border-[#e8eaed] shadow-sm focus:border-[#d88c9a] focus:ring-[#e6a4b4] transition-all duration-300 hover:shadow-md focus:shadow-lg bg-[#f6e6d1]/20 px-3"
              />
            </div>
            <div>
              <label
                htmlFor="message"
                className="block text-sm font-medium text-black transition-colors duration-300 hover:text-[#d88c9a]"
              >
                {t('message')}
              </label>
              <textarea
                id="message"
                rows={5}
                value={formData.message}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-[#e8eaed] shadow-sm focus:border-[#d88c9a] focus:ring-[#e6a4b4] transition-all duration-300 hover:shadow-md focus:shadow-lg bg-[#f6e6d1]/20 py-3 px-3"
              />
            </div>
            
            {/* Status message */}
            {submitStatus.type && (
              <div 
                className={`p-3 rounded ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}
              >
                {submitStatus.message}
              </div>
            )}
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 bg-gradient-to-r from-[#d88c9a] to-[#e6a4b4] text-white py-3 px-4 rounded-md transition-all duration-300 hover:from-[#c67c8a] hover:to-[#d694a4] hover:shadow-lg active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t('sending') : t('send')}
            </button>
          </div>

          {/* Éléments décoratifs */}
          <div className="mt-8 flex justify-center space-x-4">
            <div className="w-3 h-3 rounded-full bg-[#f6e6d1] animate-pulse"></div>
            <div className="w-3 h-3 rounded-full bg-[#d88c9a] animate-pulse delay-75"></div>
            <div className="w-3 h-3 rounded-full bg-[#86a3b8] animate-pulse delay-150"></div>
          </div>
        </form>

        {/* Info de contact avec icônes */}
        <div className="mt-12 max-w-lg mx-auto bg-white/90 backdrop-blur-sm p-6 rounded-xl shadow-md border border-[#f6e6d1]">
          <h2 className="text-2xl font-bold text-black mb-4">{t('our_information')}</h2>
          <div className="space-y-3">
            <p className="flex items-center text-black">
              <span className="mr-2 text-[#d88c9a]">📍</span> {t('address')}
            </p>
            <p className="flex items-center text-black">
              <span className="mr-2 text-[#d88c9a]">📞</span> {t('phone')}
            </p>
            <p className="flex items-center text-black">
              <span className="mr-2 text-[#d88c9a]">✉️</span> {t('email_contact')}
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;

// // pages/Contact.tsx
// import React, { useState } from 'react';
// import Navbar from '../Components/Navbar';
// import { Footer } from '../Components/Footer';

// interface FormData {
//   name: string;
//   email: string;
//   message: string;
// }

// export const Contact: React.FC = () => {
//   const [formData, setFormData] = useState<FormData>({
//     name: '',
//     email: '',
//     message: ''
//   });
//   const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
//   const [submitStatus, setSubmitStatus] = useState<{
//     type: 'success' | 'error' | null;
//     message: string;
//   }>({ type: null, message: '' });

//   const handleChange = (
//     e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
//   ) => {
//     const { id, value } = e.target;
//     setFormData((prev) => ({
//       ...prev,
//       [id]: value
//     }));
//   };

//   const handleSubmit = async (e: React.FormEvent) => {
//     e.preventDefault();
//     setIsSubmitting(true);
//     setSubmitStatus({ type: null, message: '' });
    
//     try {
//       // API call to submit form data
//       // const response = await fetch('https://api.example.com/contact', {
//       //   method: 'POST',
//       //   headers: {
//       //     'Content-Type': 'application/json'
//       //   },
//       //   body: JSON.stringify(formData)
//       // });
      
//       // Simulating API call for demonstration
//       await new Promise((resolve) => setTimeout(resolve, 1000));
      
//       // if (!response.ok) throw new Error('Failed to submit form');
//       // const data = await response.json();
      
//       setSubmitStatus({
//         type: 'success',
//         message: 'Your message has been sent successfully!'
//       });
      
//       // Reset form after successful submission
//       setFormData({ name: '', email: '', message: '' });
//     } catch (error) {
//       console.error('Error submitting form:', error);
//       setSubmitStatus({
//         type: 'error',
//         message: 'Failed to send message. Please try again later.'
//       });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   return (
//     <div className="bg-gradient-to-b from-white to-[#86a3b8] min-h-screen">
//       <Navbar />
//       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
//         {/* Titre avec effets doux */}
//         <h1 className="text-5xl md:text-6xl font-extrabold text-center mb-12">
//           <span className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-[#d88c9a] via-[#e6a4b4] to-[#86a3b8] animate-[pulse_3s_ease-in-out_infinite]">
//             Contact-
//             <span className="relative inline-block">
//               us
//               <span className="absolute -top-2 -right-2 w-2 h-2 bg-[#d88c9a] rounded-full animate-ping opacity-75"></span>
//             </span>
//           </span>
//           <span className="block mt-2 h-1 w-24 mx-auto bg-gradient-to-r from-[#d88c9a] to-[#86a3b8] rounded-full animate-[pulse_4s_ease-in-out_infinite]"></span>
//         </h1>

//         {/* Formulaire avec nouvelle palette de couleurs */}
//         <form
//           onSubmit={handleSubmit}
//           className="max-w-lg mx-auto bg-white p-8 rounded-xl shadow-md border border-[#f6e6d1] transform transition-all duration-500 hover:shadow-xl"
//         >
//           <div className="space-y-6">
//             <div>
//               <label
//                 htmlFor="name"
//                 className="block text-sm font-medium text-black transition-colors duration-300 hover:text-[#d88c9a]"
//               >
//                 Name
//               </label>
//               <input
//                 type="text"
//                 id="name"
//                 value={formData.name}
//                 onChange={handleChange}
//                 required
//                 className="mt-1 block w-full h-12 rounded-md border-[#e8eaed] shadow-sm focus:border-[#d88c9a] focus:ring-[#e6a4b4] transition-all duration-300 hover:shadow-md focus:shadow-lg bg-[#f6e6d1]/20 px-3"
//               />
//             </div>
//             <div>
//               <label
//                 htmlFor="email"
//                 className="block text-sm font-medium text-black transition-colors duration-300 hover:text-[#d88c9a]"
//               >
//                 Email
//               </label>
//               <input
//                 type="email"
//                 id="email"
//                 value={formData.email}
//                 onChange={handleChange}
//                 required
//                 className="mt-1 block w-full h-12 rounded-md border-[#e8eaed] shadow-sm focus:border-[#d88c9a] focus:ring-[#e6a4b4] transition-all duration-300 hover:shadow-md focus:shadow-lg bg-[#f6e6d1]/20 px-3"
//               />
//             </div>
//             <div>
//               <label
//                 htmlFor="message"
//                 className="block text-sm font-medium text-black transition-colors duration-300 hover:text-[#d88c9a]"
//               >
//                 Message
//               </label>
//               <textarea
//                 id="message"
//                 rows={5}
//                 value={formData.message}
//                 onChange={handleChange}
//                 required
//                 className="mt-1 block w-full rounded-md border-[#e8eaed] shadow-sm focus:border-[#d88c9a] focus:ring-[#e6a4b4] transition-all duration-300 hover:shadow-md focus:shadow-lg bg-[#f6e6d1]/20 py-3 px-3"
//               />
//             </div>
            
//             {/* Status message */}
//             {submitStatus.type && (
//               <div 
//                 className={`p-3 rounded ${
//                   submitStatus.type === 'success' 
//                     ? 'bg-green-100 text-green-800 border border-green-200' 
//                     : 'bg-red-100 text-red-800 border border-red-200'
//                 }`}
//               >
//                 {submitStatus.message}
//               </div>
//             )}
            
//             <button
//               type="submit"
//               disabled={isSubmitting}
//               className="w-full h-12 bg-gradient-to-r from-[#d88c9a] to-[#e6a4b4] text-white py-3 px-4 rounded-md transition-all duration-300 hover:from-[#c67c8a] hover:to-[#d694a4] hover:shadow-lg active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
//             >
//               {isSubmitting ? 'Sending...' : 'Send'}
//             </button>
//           </div>

//           {/* Éléments décoratifs */}
//           <div className="mt-8 flex justify-center space-x-4">
//             <div className="w-3 h-3 rounded-full bg-[#f6e6d1] animate-pulse"></div>
//             <div className="w-3 h-3 rounded-full bg-[#d88c9a] animate-pulse delay-75"></div>
//             <div className="w-3 h-3 rounded-full bg-[#86a3b8] animate-pulse delay-150"></div>
//           </div>
//         </form>

//         {/* Info de contact avec icônes */}
//         <div className="mt-12 max-w-lg mx-auto bg-white/90 backdrop-blur-sm p-6 rounded-xl shadow-md border border-[#f6e6d1]">
//           <h2 className="text-2xl font-bold text-black mb-4">Our Information</h2>
//           <div className="space-y-3">
//             <p className="flex items-center text-black">
//               <span className="mr-2 text-[#d88c9a]">📍</span> 123 Beige Avenue, City
//             </p>
//             <p className="flex items-center text-black">
//               <span className="mr-2 text-[#d88c9a]">📞</span> (123) 456-7890
//             </p>
//             <p className="flex items-center text-black">
//               <span className="mr-2 text-[#d88c9a]">✉️</span> hello@example.com
//             </p>
//           </div>
//         </div>
//       </div>
//       <Footer />
//     </div>
//   );
// };

// export default Contact;